<template>
  <PageContainer>
    <View gap="lg" direction="column">
      <!-- On a phone the scope strip slides between the title and the page actions -->
      <div class="page-header">
        <div class="page-title">
          <!-- The title and the strip hold their space until the team data is in, so nothing jumps. The
               title only names the scope; switching and managing live on the strip under it -->
          <Skeleton v-if="teamPending" width="16rem" height="2.25rem" />
          <template v-else>
            <Text v-if="portfolios.length" variant="label-small" color="secondary" as="div" class="eyebrow">
              Your properties
            </Text>
            <Text variant="h1" class="title" data-testid="portfolio-title">{{ pageTitle }}</Text>
          </template>
        </div>

        <View gap="sm" align="end" class="page-actions">
          <Button variant="outline" size="medium" @click="openDownloadModal">
            <Icon name="download-cloud-02" size="medium" />
            <span>Download leads</span>
          </Button>

          <!-- Each tab adds its own kind; one started from a portfolio's page starts out in that portfolio -->
          <Button
            v-if="tab === 'buildings'"
            size="medium"
            :to="newBuildingRoute(newListingQuery)"
            data-testid="add-building"
          >
            <Icon name="plus" />
            <span>Add building</span>
          </Button>
          <Button v-else size="medium" :to="newListingRoute(newListingQuery)" data-testid="add-listing">
            <Icon name="plus" />
            <span>Add home</span>
          </Button>
        </View>

        <div v-if="teamPending || showStrip" class="page-scope">
          <Skeleton v-if="teamPending" height="4.75rem" radius="lg" />
          <PortfolioScopeStrip
            v-else
            v-model="portfolioFilter"
            :portfolios="portfolios"
            :members="members"
            :counts="holdingsByPortfolio"
            :total="holdings"
            :can-file="canFileUnassigned"
            @manage="goToManage"
            @create="createPanelRef?.open()"
            @file="filePropertiesModalRef?.open()"
            @add="filePropertiesModalRef?.open(selectedPortfolio)"
            @dismiss="dismissIntro"
          />
        </div>
      </div>

      <View direction="column" gap="lg">
        <!-- The filter narrows the list; the toggle only changes how it reads, cards or rows -->
        <div class="tabs-row">
          <NavPanel variant="secondary" :links="tabLinks" />

          <View gap="sm" align="center" class="tools">
            <FilterMenu
              v-model="statusFilter"
              label="Status"
              :options="statusOptions"
              :full-width="screenMobile"
              mobile-sheet
              data-testid="status-filter"
            />

            <ListViewToggle list="properties" />
          </View>
        </div>

        <View v-if="state.status === 'pending'" justify="center" padding="xl">
          <LoadingIndicator />
        </View>

        <EmptyPagePlaceholder
          v-else-if="state.status === 'error'"
          title="Could not load portfolio"
          description="Please reload the page or try again later"
        />

        <!-- A portfolio with nothing in it yet: the strip above carries the next step -->
        <EmptyState
          v-else-if="selectedPortfolio && isSelectedPortfolioEmpty"
          icon="home-03"
          :title="`${selectedPortfolio.name} is empty`"
          :description="canCreateIntoSelected ? emptyPortfolioHint : 'Its owner or an admin can add properties to it.'"
        />

        <template v-else-if="tab === 'buildings'">
          <EmptyState
            v-if="!buildings.length"
            icon="building-06"
            title="No buildings yet"
            description="A building holds its address once; each unit inside gets its own listing."
          >
            <template #action>
              <Button size="medium" :to="newBuildingRoute(newListingQuery)">
                <Icon name="plus" size="small" />
                <span>Add your first building</span>
              </Button>
            </template>
          </EmptyState>

          <EmptyPagePlaceholder
            v-else-if="!filteredBuildings.length"
            title="No buildings found"
            :description="buildingsEmptyDescription"
          />

          <BuildingTable
            v-else-if="showTable"
            :buildings="filteredBuildings"
            :show-portfolio="!selectedPortfolio"
            :show-manager="showManagers"
            @select-portfolio="portfolioFilter = $event"
          />

          <div v-else class="property-cards-wrapper">
            <!-- The chip says where a building is filed, so it is left off once the page shows one portfolio -->
            <BuildingCard
              v-for="building in filteredBuildings"
              :key="building.id"
              :building="building"
              :show-portfolio="!selectedPortfolio"
              :show-manager="showManagers"
              @select-portfolio="portfolioFilter = $event"
            />
          </div>
        </template>

        <template v-else>
          <EmptyPagePlaceholder
            v-if="filteredProperties.length === 0"
            :title="homesEmptyState.title"
            :description="homesEmptyState.description"
          />

          <!-- The portfolio chip says where a unit is filed, so it is left off once the page shows one portfolio -->
          <PropertyTable
            v-else-if="showTable"
            :properties="filteredProperties"
            :show-portfolio="!selectedPortfolio"
            :show-manager="showManagers"
            @select-portfolio="portfolioFilter = $event"
          />

          <div v-else class="property-cards-wrapper">
            <PropertyCard
              v-for="property in filteredProperties"
              :key="property.id"
              :property="property"
              :show-portfolio="!selectedPortfolio"
              :show-manager="showManagers"
              @select-portfolio="portfolioFilter = $event"
            />
          </div>
        </template>
      </View>
    </View>
    <PortfolioDownloadInquiriesModal ref="downloadModalRef" :scope="downloadScope" />
    <CreatePortfolioPanel
      ref="createPanelRef"
      :members="members"
      :portfolios="portfolios"
      @created="portfolioFilter = $event.id"
    />
    <FilePropertiesModal
      ref="filePropertiesModalRef"
      :members="members"
      :portfolios="portfolios"
      :properties="properties"
      @create="createPanelRef?.open(unassignedPortfolioFilter)"
    />
  </PageContainer>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  Button,
  EmptyPagePlaceholder,
  EmptyState,
  FilterMenu,
  FilterMenuOption,
  Icon,
  LoadingIndicator,
  PageContainer,
  Skeleton,
  Text,
  View,
  screenMobile,
} from '@tallo/design-system';
import {
  unitsForNewPortfolio,
  unitsToFile,
  useCompanyPortfoliosQuery,
  useCompanyTeamQuery,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  AppRouteNames,
  PortfolioTab,
  managePortfoliosRoute,
  newBuildingRoute,
  newListingRoute,
} from '@tallo/investor/navigation';
import {
  BuildingCard,
  BuildingStatusFilter,
  BuildingTable,
  ListViewToggle,
  PropertyCard,
  PropertyTable,
  allPortfoliosFilter,
  buildingStatusFilterLabels,
  countByBuildingStatus,
  countByStatus,
  countHoldingsByPortfolio,
  filterByPortfolio,
  matchesBuildingStatus,
  sortByStatus,
  unassignedPortfolioFilter,
  useBuildingsQuery,
  useListView,
  usePortfolioQuery,
} from '@tallo/investor/property';
import { PropertyStatus } from '@tallo/property';

import { NavPanelLink } from '../root/nav-panel/nav-panel-link.interface';
import NavPanel from '../root/nav-panel/nav-panel.vue';
import PortfolioDownloadInquiriesModal from './portfolio-download-inquiries-modal.vue';
import PortfolioScopeStrip from './portfolio-scope-strip.vue';
import CreatePortfolioPanel from './portfolios/create-portfolio-panel.vue';
import FilePropertiesModal from './portfolios/file-properties-modal.vue';

/** The status filter's widest choice: every status a listing can be in */
const allStatuses = 'All';

// Once dismissed, the invitation to make a first portfolio stays away for this browser until one exists
const introDismissedKey = 'portfolio-intro-dismissed';

const route = useRoute();
const router = useRouter();
// The scope is in the URL (`?portfolio=<id|unassigned>`), so the manage page can open the page on one
// and a link can be shared; the page writes it back as the scope changes
const portfolioFilter = ref(scopeFromQuery(route.query.portfolio));
const introDismissed = ref(readIntroDismissed());
const { showTable } = useListView('properties');
const downloadModalRef = ref<InstanceType<typeof PortfolioDownloadInquiriesModal> | null>(null);
const createPanelRef = ref<InstanceType<typeof CreatePortfolioPanel> | null>(null);
const filePropertiesModalRef = ref<InstanceType<typeof FilePropertiesModal> | null>(null);

const { investor, isAdmin } = storeToRefs(useInvestorStore());
// The server lists the properties standing on their own here; a building's units are on its page
const { properties, state } = usePortfolioQuery();
const { buildings, status: buildingsStatus } = useBuildingsQuery();
const portfoliosQuery = useCompanyPortfoliosQuery();
const { portfolios } = portfoliosQuery;
const { members } = useCompanyTeamQuery();

const teamPending = computed(() => state.value.status === 'pending' || portfoliosQuery.status.value === 'pending');

// A lone admin already knows who runs every unit; the manager only says something once there is a team
const showManagers = computed(() => members.value.length > 1);

/** The tab in the URL; none on a bare /portfolio, which the page then picks */
const tab = computed<PortfolioTab | null>(() =>
  route.params.tab === 'homes' || route.params.tab === 'buildings' ? route.params.tab : null,
);

const selectedProperties = computed(() => filterByPortfolio(properties.value, portfolioFilter.value));
const selectedBuildings = computed(() => filterByPortfolio(buildings.value, portfolioFilter.value));
const selectedPortfolio = computed(() => portfolios.value.find(({ id }) => id === portfolioFilter.value));
const isSelectedPortfolioEmpty = computed(() => !selectedProperties.value.length && !selectedBuildings.value.length);

const holdings = computed(() => ({ homes: properties.value.length, buildings: buildings.value.length }));
const holdingsByPortfolio = computed(() => countHoldingsByPortfolio(properties.value, buildings.value));

// The download follows the page: narrowed to the portfolio or the loose units on screen, else everything
const downloadScope = computed(() => {
  if (portfolioFilter.value === allPortfoliosFilter) {
    return undefined;
  }

  if (portfolioFilter.value === unassignedPortfolioFilter) {
    return { portfolio: unassignedPortfolioFilter, name: 'unassigned' };
  }

  return selectedPortfolio.value
    ? { portfolio: selectedPortfolio.value.id, name: selectedPortfolio.value.name }
    : undefined;
});

const emptyPortfolioHint =
  'Add existing properties to it, or add a home or a building while it is open: the new one starts out in it.';

const pageTitle = computed(() => {
  if (selectedPortfolio.value) {
    return selectedPortfolio.value.name;
  }

  if (portfolioFilter.value === unassignedPortfolioFilter) {
    return 'Unassigned';
  }

  return portfolios.value.length ? 'All properties' : 'Your properties';
});

const viewer = computed(() => ({ investorId: investor.value?.id, isAdmin: isAdmin.value }));

// The strip carries the empty states too; only the invitation to a first portfolio can be waved away.
// A member gets it once there is something they run to group, since that is all theirs may hold
const showStrip = computed(
  () =>
    portfolios.value.length > 0 ||
    (!introDismissed.value &&
      (isAdmin.value || unitsForNewPortfolio(viewer.value, properties.value, portfolios.value).length > 0)),
);

// A loose unit is filed by whoever runs it: an admin, or its manager
const canFileUnassigned = computed(() => unitsToFile(viewer.value, properties.value, portfolios.value).length > 0);

// From a page showing one portfolio the new home or building starts out in it; from Unassigned, in none
const newListingQuery = computed(() =>
  portfolioFilter.value === allPortfoliosFilter ? undefined : { portfolio: portfolioFilter.value },
);

// Each tab is its own address, and moving between them keeps the portfolio on screen
const tabLinks = computed<NavPanelLink[]>(() => {
  const query = newListingQuery.value;

  return [
    {
      id: 'portfolio-tab-homes',
      appRoute: AppRouteNames.INVESTOR_PORTFOLIO,
      params: { tab: 'homes' },
      query,
      title: 'Homes',
      icon: 'home-03',
      count: selectedProperties.value.length,
    },
    {
      id: 'portfolio-tab-buildings',
      appRoute: AppRouteNames.INVESTOR_PORTFOLIO,
      params: { tab: 'buildings' },
      query,
      title: 'Buildings',
      icon: 'building-06',
      count: selectedBuildings.value.length,
    },
  ];
});

// A bare /portfolio opens on the homes, unless the company has none and has a building; it waits for
// both lists, so the choice is made once
watch(
  [tab, () => state.value.status, buildingsStatus],
  ([current, homesStatus, buildingsLoad]) => {
    if (current || route.name !== AppRouteNames.INVESTOR_PORTFOLIO) {
      return;
    }

    if (homesStatus !== 'success' || buildingsLoad !== 'success') {
      return;
    }

    const opensOn: PortfolioTab = properties.value.length === 0 && buildings.value.length > 0 ? 'buildings' : 'homes';

    router.replace({ name: AppRouteNames.INVESTOR_PORTFOLIO, params: { tab: opensOn }, query: route.query });
  },
  { immediate: true },
);

/** The tab's status choices with how many each would show, in the order a listing moves through them */
const statusOptions = computed<FilterMenuOption[]>(() => {
  if (tab.value === 'buildings') {
    const counts = countByBuildingStatus(selectedBuildings.value);

    return Object.values(BuildingStatusFilter).map((filter) => ({
      value: filter,
      label: buildingStatusFilterLabels[filter],
      count: counts[filter],
    }));
  }

  const counts = countByStatus(selectedProperties.value);
  const statuses = Object.values(PropertyStatus)
    // A syndication error is only worth a choice while there is one
    .filter((status) => status !== PropertyStatus.SYNDICATION_ERROR || counts[status])
    .map((status) => ({ value: status, label: status, count: counts[status] ?? 0 }));

  return [{ value: allStatuses, label: 'All', count: selectedProperties.value.length }, ...statuses];
});

// The status lives in the URL (`?status=`) beside the scope, one per tab: a tab link carries no status,
// so each tab opens on all of its own; a value no option offers reads as all of them
const statusFilter = computed({
  get: () => {
    const status = route.query.status;

    return typeof status === 'string' && statusOptions.value.some((option) => option.value === status)
      ? status
      : statusOptions.value[0].value;
  },
  set: (status: string) => {
    router.replace({ query: { ...route.query, status: status === statusOptions.value[0].value ? undefined : status } });
  },
});

// Listed first, rented out last, unless one status was asked for
const filteredProperties = computed(() =>
  statusFilter.value === allStatuses
    ? sortByStatus(selectedProperties.value)
    : selectedProperties.value.filter((property) => property.status === statusFilter.value),
);

const filteredBuildings = computed(() =>
  selectedBuildings.value.filter((building) =>
    matchesBuildingStatus(building, statusFilter.value as BuildingStatusFilter),
  ),
);

// Nothing on the buildings tab: because the filter hides them, because the scope holds none, or because none is loose
const buildingsEmptyDescription = computed(() => {
  if (selectedBuildings.value.length) {
    return "Adjust your filters to find what you're looking for";
  }

  return portfolioFilter.value === unassignedPortfolioFilter
    ? 'Every building is in a portfolio'
    : `None ${selectedPortfolio.value ? 'in this portfolio' : 'yet'}`;
});

// Both directions stop at this page's own route: on the way out the next route's query is not ours to rewrite
watch(portfolioFilter, (scope) => {
  const portfolio = scope === allPortfoliosFilter ? undefined : scope;

  if (route.name === AppRouteNames.INVESTOR_PORTFOLIO && route.query.portfolio !== portfolio) {
    router.replace({ query: { ...route.query, portfolio } });
  }
});

watch(
  () => route.query.portfolio,
  (query) => {
    const scope = scopeFromQuery(query);

    if (route.name === AppRouteNames.INVESTOR_PORTFOLIO && scope !== portfolioFilter.value) {
      portfolioFilter.value = scope;
    }
  },
);

// A portfolio that was deleted, or that never loaded, cannot stay selected
watch(portfolios, (loaded) => {
  const selected = portfolioFilter.value;

  if (
    selected !== allPortfoliosFilter &&
    selected !== unassignedPortfolioFilter &&
    !loaded.some(({ id }) => id === selected)
  ) {
    portfolioFilter.value = allPortfoliosFilter;
  }
});

const homesEmptyState = computed(() => {
  if (selectedProperties.value.length) {
    return { title: 'No properties found', description: "Adjust your filters to find what you're looking for" };
  }

  if (portfolioFilter.value === unassignedPortfolioFilter) {
    return { title: 'Every property is in a portfolio', description: 'Nothing is left unassigned right now' };
  }

  return { title: 'No properties found', description: "Adjust your filters to find what you're looking for" };
});

// Who may create straight into a portfolio: an admin, its owner, or one of its default collaborators
const canCreateIntoSelected = computed(() => {
  const portfolio = selectedPortfolio.value;

  if (!portfolio) {
    return false;
  }

  return (
    isAdmin.value ||
    portfolio.ownerId === investor.value?.id ||
    portfolio.collaborators.some(({ investorId, joinsNewUnits }) => investorId === investor.value?.id && joinsNewUnits)
  );
});

function openDownloadModal() {
  downloadModalRef.value?.open();
}

function goToManage() {
  router.push(managePortfoliosRoute());
}

function scopeFromQuery(query: unknown): string {
  return typeof query === 'string' && query ? query : allPortfoliosFilter;
}

// Browser storage can be missing or blocked (private windows, cleared site data); the page works without it
function readIntroDismissed(): boolean {
  try {
    return localStorage.getItem(introDismissedKey) === 'true';
  } catch {
    return false;
  }
}

function dismissIntro() {
  introDismissed.value = true;

  try {
    localStorage.setItem(introDismissedKey, 'true');
  } catch {
    // Nothing to keep it in; the strip comes back on the next visit
  }
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.page-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: var(--t-spacing-md);
  row-gap: var(--t-spacing-xl);
  align-items: center;

  @include screen-max-sm {
    grid-template-columns: minmax(0, 1fr);
    row-gap: var(--t-spacing-md);
  }
}

.page-title {
  min-width: 0;
}

.eyebrow {
  margin-bottom: var(--t-spacing-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.title {
  overflow-wrap: anywhere;

  // On a phone a long name takes one line and is cut; the full name is in the strip's Switch sheet
  @include screen-max-sm {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

// On a phone the two actions share a row
.page-actions {
  @include screen-max-sm {
    > * {
      flex: 1;
    }
  }
}

.page-scope {
  grid-column: 1 / -1;
}

// The strip moves up between the title and the actions, where a thumb finds it first; pinned alone,
// so without a strip the actions follow the title with no empty row between
@include screen-max-sm {
  .page-scope {
    grid-row: 2;
  }
}

// The tabs take the left and sit on a rule the selected tab's own line lands on; the tools the right
.tabs-row {
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: var(--t-spacing-md);
  border-bottom: 1px solid var(--t-color-border-secondary);

  @include screen-max-sm {
    flex-direction: column;
    align-items: stretch;
  }

  // The tools sit a little above the rule
  .tools {
    margin-bottom: var(--t-spacing-sm);

    @include screen-max-sm {
      margin-bottom: var(--t-spacing-md);
    }
  }
}

// On a phone the filter takes the width; the toggle is away there, since only cards fit
.tools {
  @include screen-max-sm {
    flex-direction: column;
    align-items: stretch;
  }
}

.property-cards-wrapper {
  display: grid;
  gap: var(--t-spacing-md);
  grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));

  @include screen-max-sm {
    grid-template-columns: 1fr;
  }
}
</style>
